import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import DatePicker from '../DatePicker';
import { AlertModal } from '../AlertModal';
import { IconEye, IconEyeOff } from '../Icons';
import { PREFIX_COLOR_OPTIONS, normalizePrefixColor, getPrefixColorOption, seasonToForm, formToPayload, saveErrorText } from './seasonForm';
import './seasons.css';

// ── small building blocks ──
export function Toggle({ on, onChange, disabled, label }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} disabled={disabled}
      className={`sp-toggle${on ? ' on' : ''}`} onClick={() => !disabled && onChange(!on)}>
      <span />
    </button>
  );
}

function ToggleRow({ label, desc, on, onChange, disabled, warn }) {
  return (
    <div className={`sp-row${disabled ? ' disabled' : ''}`}>
      <div className="sp-row-text">
        <div className="sp-row-label">{label}</div>
        {desc && <div className="sp-row-desc">{desc}</div>}
        {warn && <div className="sp-row-warn">{warn}</div>}
      </div>
      <Toggle on={on} onChange={onChange} disabled={disabled} label={label} />
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div className="sp-field">
      <label className="sp-label">{label}{required && <span className="sp-req"> *</span>}</label>
      {children}
      {hint && <div className="sp-hint">{hint}</div>}
    </div>
  );
}

function Segmented({ value, options, onChange }) {
  return (
    <div className="sp-seg">
      {options.map(o => (
        <button key={o.value} type="button" className={value === o.value ? 'active' : ''} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/**
 * Full season settings (the season page's Settings tab). Same fields and payload as the old modal,
 * grouped into sections with a side nav and one sticky Save bar.
 * @param {{ season: object, onSaved: (season) => void, onDirtyChange?: (dirty: boolean) => void }} props
 */
export default function SeasonSettingsForm({ season, onSaved, onDirtyChange }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(() => seasonToForm(season));
  const [tempAdminInput, setTempAdminInput] = useState({ steam_id: '', name: '' });
  const [baseline, setBaseline] = useState(() => JSON.stringify(formToPayload(seasonToForm(season))));
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showAkrosKey, setShowAkrosKey] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [active, setActive] = useState('basics');
  const savingRef = useRef(false);
  const warmupRefs = useRef([]);
  const knifeLiveRefs = useRef([]);
  const sectionRefs = useRef({});

  const set = (patch) => setFormData(prev => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));

  const dirty = useMemo(() => JSON.stringify(formToPayload(formData, tempAdminInput)) !== baseline, [formData, tempAdminInput, baseline]);
  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  // warn before leaving the page with unsaved changes
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const SECTIONS = [
    { id: 'basics', label: t('seasonPage.secBasics', 'Basics') },
    { id: 'rules', label: t('createSeason.matchRules', 'Match Rules') },
    { id: 'veto', label: t('match.mapVeto', 'Map Veto') },
    { id: 'broadcast', label: t('createSeason.broadcastPublic', 'Broadcast & Public') },
    { id: 'chat', label: t('seasonPage.secChat', 'Chat prefix') },
    { id: 'admins', label: t('seasons.tempAdmins', 'Temp admins') },
    { id: 'commands', label: t('createSeason.customCommands', 'Custom Commands (CVARs)') },
    { id: 'halftime', label: t('createSeason.halftimeBreak', 'Halftime Break') },
    { id: 'integrations', label: t('seasonPage.secIntegrations', 'Integrations') },
  ];

  // highlight the section in view
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.dataset.section);
    }, { rootMargin: '-15% 0px -70% 0px' });
    Object.values(sectionRefs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);
  const goTo = (id) => { const el = sectionRefs.current[id]; if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActive(id); };
  const Section = ({ id, title, desc, children }) => (
    <section className="sp-section" id={`season-${id}`} data-section={id} ref={el => { sectionRefs.current[id] = el; }}>
      <div className="sp-section-head">
        <h3>{title}</h3>
        {desc && <p>{desc}</p>}
      </div>
      <div className="sp-section-body">{children}</div>
    </section>
  );

  const save = async (e) => {
    if (e) e.preventDefault();
    if (savingRef.current) return;
    if (!formData.name.trim()) return setAlertModal({ show: true, message: t('seasonPage.nameRequired', 'Season name is required'), type: 'error' });
    if (!formData.start_date) return setAlertModal({ show: true, message: t('seasonPage.startRequired', 'Start date is required'), type: 'error' });
    if (!formData.end_date) return setAlertModal({ show: true, message: t('seasonPage.endRequired', 'End date is required'), type: 'error' });
    savingRef.current = true; setSaving(true);
    try {
      const res = await axios.put(`/api/seasons/${season.id}`, formToPayload(formData, tempAdminInput));
      const next = seasonToForm(res.data);
      setFormData(next);
      setTempAdminInput({ steam_id: '', name: '' });
      setBaseline(JSON.stringify(formToPayload(next)));
      setSavedAt(Date.now());
      if (onSaved) onSaved(res.data);
    } catch (err) {
      setAlertModal({ show: true, message: saveErrorText(err, t), type: 'error' });
    } finally {
      savingRef.current = false; setSaving(false);
    }
  };
  const discard = () => { setFormData(seasonToForm(season)); setTempAdminInput({ steam_id: '', name: '' }); };

  // ── CVAR helpers (unchanged behaviour: Tab on the last field adds one, max 20) ──
  const updateCvar = useCallback((key, i, v) => setFormData(prev => { const arr = [...prev[key]]; arr[i] = v; return { ...prev, [key]: arr }; }), []);
  const removeCvar = useCallback((key, i) => setFormData(prev => { const arr = prev[key].filter((_, j) => j !== i); return { ...prev, [key]: arr.length ? arr : [''] }; }), []);
  const addCvar = useCallback((key) => setFormData(prev => ({ ...prev, [key]: [...prev[key], ''] })), []);
  const cvarKeyDown = (e, key, i, refs) => {
    if (e.key === 'Tab' && !e.shiftKey && i === formData[key].length - 1 && formData[key].length < 20) {
      e.preventDefault(); addCvar(key);
      setTimeout(() => { if (refs.current[i + 1]) refs.current[i + 1].focus(); }, 50);
    }
  };
  const renderCvars = (key, label, refs) => (
    <div>
      <div className="sp-label">{label}</div>
      {formData[key].map((cmd, i) => (
        <div key={i} className="sp-cvar">
          <input ref={el => { refs.current[i] = el; }} className="sp-input mono" type="text" placeholder={`command ${i + 1}`} value={cmd}
            maxLength={256} onChange={e => updateCvar(key, i, e.target.value)} onKeyDown={e => cvarKeyDown(e, key, i, refs)} />
          {formData[key].length > 1 && <button type="button" className="sp-x" aria-label={t('common.remove', 'Remove')} onClick={() => removeCvar(key, i)}>×</button>}
        </div>
      ))}
      {formData[key].length < 20 && <button type="button" className="sp-link" onClick={() => addCvar(key)}>+ {t('common.add', 'Add')}</button>}
    </div>
  );

  const tsConfigured = formData.ts_enabled && formData.ts_host && formData.ts_query_user && formData.ts_query_password;
  const prefixOpt = getPrefixColorOption(formData.chat_prefix_color);

  const addTempAdmin = () => {
    const sid = tempAdminInput.steam_id.trim();
    if (!sid) return;
    const list = formData.temp_admins || [];
    if (list.length >= 20) { setAlertModal({ show: true, message: t('seasons.tempAdminTooMany', 'A season can have at most 20 temp admins.'), type: 'error' }); return; }
    if (!list.some(a => a.steam_id === sid)) set({ temp_admins: [...list, { steam_id: sid, name: tempAdminInput.name.trim() }] });
    setTempAdminInput({ steam_id: '', name: '' });
  };

  return (
    <form className="sp-settings" onSubmit={save}>
      <nav className="sp-nav" aria-label={t('seasonPage.settingsNav', 'Settings sections')}>
        {SECTIONS.map(s => (
          <button key={s.id} type="button" className={active === s.id ? 'active' : ''} onClick={() => goTo(s.id)}>{s.label}</button>
        ))}
      </nav>

      <div className="sp-sections">
        {Section({ id: 'basics', title: t('seasonPage.secBasics', 'Basics'), desc: t('createSeason.subtitle', 'Name, match format & rules'), children: (
          <>
            <Field label={t('createSeason.seasonName', 'Season Name')} required>
              <input className="sp-input" type="text" value={formData.name} placeholder={t('createSeason.seasonNamePlaceholder', 'e.g. LOT League S1')} onChange={e => set({ name: e.target.value })} />
            </Field>
            <div className="sp-grid2">
              <Field label={t('createSeason.gameMode', 'Game Mode')}>
                <Segmented value={formData.mode} onChange={v => set({ mode: v })} options={[{ value: 'active_duty', label: 'Active Duty' }, { value: 'wingman', label: 'Wingman' }]} />
              </Field>
              <Field label={t('createSeason.matchFormat', 'Match Format')}>
                <Segmented value={formData.best_of} onChange={v => set({ best_of: v })} options={[1, 3, 5].map(n => ({ value: n, label: `BO${n}` }))} />
              </Field>
            </div>
            <Field label={t('createSeason.seasonDuration', 'Season Duration')} required>
              <div className="sp-grid2">
                <DatePicker label={t('createSeason.startDate', 'Start Date')} value={formData.start_date} onChange={v => set({ start_date: v })} />
                <DatePicker label={t('createSeason.endDate', 'End Date')} value={formData.end_date} onChange={v => set({ end_date: v })} minDate={formData.start_date} />
              </div>
            </Field>
          </>
        ) })}

        {Section({ id: 'rules', title: t('createSeason.matchRules', 'Match Rules'), children: (
          <>
            {[
              { key: 'admin_only_start', label: t('createMatch.adminOnlyStart', 'Admin Only Start'), desc: t('createMatch.adminOnlyStartHint', "Players can't use .ready") },
              { key: 'enforce_teams', label: t('createMatch.enforceRosters', 'Enforce Rosters'), desc: t('createMatch.enforceRostersHint', 'Kick players not on roster') },
              { key: 'allow_stop', label: t('createMatch.allowStop', 'Allow !stop Command'), desc: t('createMatch.allowStopHint', 'Players can request a stop') },
              { key: 'skip_backup_load', label: t('createMatch.skipBackup', 'Skip Backup Load'), desc: t('createMatch.skipBackupHint', 'Skip auto team backup on match start') },
              { key: 'custom_avatars', label: t('createMatch.customAvatars', 'Custom Avatars'), desc: t('createMatch.customAvatarsHint', 'Deploy player avatars to server') },
              { key: 'random_password', label: t('createMatch.randomPassword', 'Random Password Protection'), desc: t('createMatch.randomPasswordHint', 'Sets a random city name as the server password') },
              { key: 'restore_admin_unpause', label: t('createSeason.restoreAdminUnpause', 'Admin unpause after round restore'), desc: t('createSeason.restoreAdminUnpauseHint', "After a round restore (also rehost, migration, map replay) only an admin can unpause (!fup or the panel's Unpause). Off: both teams type .unpause.") },
              { key: 'stage_seating', label: t('createSeason.stageSeating', 'Stage seating'), desc: t('createSeason.stageSeatingHint', 'Which team sits on which side of the stage and who sits where: a seating step in Create Match and on the match page, also in the API and live scoreboard. Off: matches of this season have no seating.') },
            ].map(r => <ToggleRow key={r.key} label={r.label} desc={r.desc} on={formData[r.key]} onChange={v => set({ [r.key]: v })} />)}
            <div className="sp-callout">
              <Field label={t('createSeason.techPauseBudget', 'Tech Pause Time Budget')} hint={t('createSeason.techBudgetHint', 'Maximum total duration each team can use for technical pauses during a match. 0 = unlimited.')}>
                <div className="sp-inline">
                  <input className="sp-input sp-num" type="number" min="0" max="7200" value={formData.tech_budget_seconds} onChange={e => set({ tech_budget_seconds: parseInt(e.target.value, 10) || 0 })} />
                  <span className="sp-muted">{t('createSeason.seconds', 'sec')}</span>
                  {formData.tech_budget_seconds > 0 && (
                    <span className="sp-accent">{Math.floor(formData.tech_budget_seconds / 60)}:{(formData.tech_budget_seconds % 60).toString().padStart(2, '0')} {t('seasonPage.minutes', 'minutes')}</span>
                  )}
                </div>
              </Field>
            </div>
          </>
        ) })}

        {Section({ id: 'veto', title: t('match.mapVeto', 'Map Veto'), children: (
          <>
            <ToggleRow label={t('match.mapVeto', 'Map Veto')} desc={t('createSeason.mapVetoHint', 'Teams ban/pick maps before match')}
              on={formData.veto_enabled} onChange={v => set({ veto_enabled: v, veto_style: v ? (formData.veto_style || 'vrs') : '' })} />
            {formData.veto_enabled && (
              <>
                <Field label={t('createMatch.vetoStyle', 'Veto Style')}>
                  <Segmented value={formData.veto_style} onChange={v => set({ veto_style: v })} options={[{ value: 'vrs', label: 'VRS Type' }, { value: 'faceit', label: 'Faceit Style' }]} />
                </Field>
                <ToggleRow label={t('createSeason.coinFlipLabel', 'Coin Flip (random first pick)')} on={formData.veto_use_coin_flip} onChange={v => set({ veto_use_coin_flip: v })} />
              </>
            )}
            <ToggleRow label={t('createMatch.steamLoginRequired', 'Steam Login Required for Veto')} desc={t('createMatch.steamLoginRequiredHint', 'Players must login with Steam to participate in veto')}
              on={formData.steam_login_required} disabled={!formData.veto_enabled} onChange={v => set({ steam_login_required: v })} />
          </>
        ) })}

        {Section({ id: 'broadcast', title: t('createSeason.broadcastPublic', 'Broadcast & Public'), children: (
          <>
            {[
              { key: 'show_killfeed', label: t('createSeason.showKillfeed', 'Show Killfeed'), desc: t('createSeason.showKillfeedHint', 'Public killfeed visible on match pages') },
              { key: 'cstv1_public', label: t('createSeason.cstv1Public', 'CSTV 1 Public'), desc: t('createSeason.cstv1PublicHint', 'Show CSTV 1 connect info on the public match page') },
              { key: 'cstv2_public', label: t('createSeason.cstv2Public', 'CSTV 2 Public'), desc: t('createSeason.cstv2PublicHint', 'Show CSTV 2 connect info on the public match page') },
            ].map(r => <ToggleRow key={r.key} label={r.label} desc={r.desc} on={formData[r.key]} onChange={v => set({ [r.key]: v })} />)}
            <div className="sp-row">
              <div className="sp-row-text">
                <div className="sp-row-label">{t('createSeason.streamDelay', 'Stream Broadcast Delay')}</div>
                <div className="sp-row-desc">{t('createSeason.streamDelayHint', 'Delay public score & killfeed by N seconds (0 = off)')}</div>
              </div>
              <div className="sp-inline">
                <input className="sp-input sp-num" type="number" min="0" max="300" value={formData.stream_delay}
                  onChange={e => set({ stream_delay: Math.min(300, Math.max(0, parseInt(e.target.value, 10) || 0)) })} />
                <span className="sp-muted">{t('createSeason.seconds', 'sec')}</span>
              </div>
            </div>
          </>
        ) })}

        {Section({ id: 'chat', title: t('seasonPage.secChat', 'Chat prefix'), desc: t('createSeason.chatPrefixHint', 'Prefix for admin chat messages in-game. Leave empty to use your default.'), children: (
          <>
            <Field label={t('createSeason.chatPrefix', 'Server Chat Prefix')}>
              <input className="sp-input mono" type="text" maxLength={30} value={formData.chat_prefix} placeholder={t('createSeason.chatPrefixPlaceholder', 'e.g. [LOT League]')} onChange={e => set({ chat_prefix: e.target.value })} />
            </Field>
            <Field label={t('createSeason.prefixColor', 'Prefix Color')}>
              <div className="sp-colors">
                {PREFIX_COLOR_OPTIONS.map(opt => {
                  const on = normalizePrefixColor(formData.chat_prefix_color) === opt.byte;
                  return (
                    <button key={opt.key} type="button" title={opt.name} className={on ? 'active' : ''} onClick={() => set({ chat_prefix_color: opt.byte })}
                      style={on ? { background: `${opt.hex}18`, borderColor: opt.hex, color: opt.hex } : undefined}>
                      <span className="sp-dot" style={{ background: opt.hex }} />{opt.name}
                    </button>
                  );
                })}
              </div>
              <div className="sp-preview">
                {t('createSeason.preview', 'Preview')}: <span style={{ color: prefixOpt.hex, fontWeight: 700 }}>{formData.chat_prefix || '[Flux]'}</span> <span className="sp-preview-msg">{t('createSeason.matchIsReady', 'Match is ready.')}</span>
              </div>
            </Field>
          </>
        ) })}

        {Section({ id: 'admins', title: t('seasons.tempAdmins', 'Temp admins'), desc: t('seasons.tempAdminsHint', "Added to every match of this season as a spectator with the in-game admin commands (!fp, !fup, !restore, !asay, !start). Changes also apply to this season's pending and live matches. Revoked when a match ends."), children: (
          <>
            {(formData.temp_admins || []).length > 0 && (
              <div className="sp-list">
                {formData.temp_admins.map((a, i) => (
                  <div key={a.steam_id + i} className="sp-list-item">
                    <span className="sp-list-name">{a.name || t('seasons.tempAdminNameFromSteam', 'Name from Steam')}</span>
                    <span className="sp-list-id">{a.steam_id}</span>
                    <button type="button" className="sp-x" aria-label={t('seasons.tempAdminRemove', 'Remove temp admin')} title={t('seasons.tempAdminRemove', 'Remove temp admin')}
                      onClick={() => set(prev => ({ temp_admins: prev.temp_admins.filter((_, j) => j !== i) }))}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div className="sp-add">
              <input className="sp-input" value={tempAdminInput.steam_id} placeholder={t('seasons.tempAdminSteamPlaceholder', 'Steam64, STEAM_0:… or profile link')}
                onChange={e => setTempAdminInput({ ...tempAdminInput, steam_id: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTempAdmin(); } }} />
              <input className="sp-input" value={tempAdminInput.name} placeholder={t('seasons.tempAdminNamePlaceholder', 'Name (optional - from Steam)')}
                onChange={e => setTempAdminInput({ ...tempAdminInput, name: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTempAdmin(); } }} />
              <button type="button" className="sp-btn" disabled={!tempAdminInput.steam_id.trim()} onClick={addTempAdmin}>{t('seasons.tempAdminAdd', 'Add')}</button>
            </div>
          </>
        ) })}

        {Section({ id: 'commands', title: t('createSeason.customCommands', 'Custom Commands (CVARs)'), desc: t('createSeason.cvarHint', 'Tab on last field to add more. Max 20 per state.'), children: (
          <div className="sp-grid2">
            {renderCvars('cvars_warmup', t('createMatch.warmupCommands', 'Warmup Commands'), warmupRefs)}
            {renderCvars('cvars_knife_live', t('createMatch.knifeLiveCommands', 'Knife / Live Commands'), knifeLiveRefs)}
          </div>
        ) })}

        {Section({ id: 'halftime', title: t('createSeason.halftimeBreak', 'Halftime Break'), children: (
          <>
            <ToggleRow label={t('createSeason.enableHalftime', 'Enable Halftime Auto-Pause')} desc={t('createSeason.enableHalftimeHint', 'Pause the match at regulation halftime (after round 12)')}
              on={formData.halftime_break_enabled} onChange={v => set({ halftime_break_enabled: v, ...(!v ? { halftime_coach_talk: false } : {}) })} />
            {formData.halftime_break_enabled && (
              <>
                <Field label={t('createSeason.breakDuration', 'Break Duration')}>
                  <select className="sp-input" value={formData.halftime_break_duration} onChange={e => set({ halftime_break_duration: parseInt(e.target.value, 10) || 5 })}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => <option key={v} value={v}>{t('seasonPage.nMinutes', '{{count}} minute(s)', { count: v })}</option>)}
                  </select>
                </Field>
                <ToggleRow label={t('createSeason.allowCoachTalk', 'Allow Coach Talk')} desc={t('createSeason.allowCoachTalkHint', 'Unmute coaches via TeamSpeak during the halftime break')}
                  warn={!tsConfigured ? t('createSeason.requiresTeamSpeak', 'Requires TeamSpeak Integration enabled & configured') : null}
                  on={formData.halftime_coach_talk} disabled={!tsConfigured} onChange={v => set({ halftime_coach_talk: tsConfigured ? v : false })} />
                <div className="sp-hint">{t('seasonPage.halftimeNote', 'Auto-pauses the server at regulation halftime only (e.g. 6-6, 7-5, 12-0). Does not trigger in overtime. Reminders are sent each minute during the break.')}</div>
              </>
            )}
          </>
        ) })}

        {Section({ id: 'integrations', title: t('seasonPage.secIntegrations', 'Integrations'), children: (
          <>
            <div className="sp-sub">{t('seasonPage.teamspeak', 'TeamSpeak coach mute')}</div>
            <ToggleRow label={t('createSeason.enableTeamSpeak', 'Enable TeamSpeak Integration')} desc={t('createSeason.enableTeamSpeakHint', 'Auto mute/unmute coaches during matches')}
              on={formData.ts_enabled} onChange={v => set({ ts_enabled: v, ...(!v ? { halftime_coach_talk: false } : {}) })} />
            {formData.ts_enabled && (
              <>
                <div className="sp-grid2">
                  <Field label={t('createSeason.serverQueryHost', 'ServerQuery Host')}>
                    <input className="sp-input" type="text" placeholder="e.g. ts.example.com" value={formData.ts_host} onChange={e => set({ ts_host: e.target.value, ...(!e.target.value ? { halftime_coach_talk: false } : {}) })} />
                  </Field>
                  <Field label={t('createSeason.queryPort', 'Query Port')}>
                    <input className="sp-input" type="number" placeholder="10011" value={formData.ts_query_port} onChange={e => set({ ts_query_port: parseInt(e.target.value) || 10011 })} />
                  </Field>
                  <Field label={t('createSeason.queryUsername', 'Query Username')}>
                    <input className="sp-input" type="text" placeholder="serveradmin" value={formData.ts_query_user} onChange={e => set({ ts_query_user: e.target.value, ...(!e.target.value ? { halftime_coach_talk: false } : {}) })} />
                  </Field>
                  <Field label={t('createSeason.queryPassword', 'Query Password')}>
                    <input className="sp-input" type="password" placeholder="••••••••" value={formData.ts_query_password} onChange={e => set({ ts_query_password: e.target.value, ...(!e.target.value ? { halftime_coach_talk: false } : {}) })} />
                  </Field>
                  <Field label={t('createSeason.voicePort', 'Voice Port')}>
                    <input className="sp-input" type="number" placeholder="9987" value={formData.ts_virtual_server_id} onChange={e => set({ ts_virtual_server_id: parseInt(e.target.value) || 9987 })} />
                  </Field>
                  <div className="sp-field sp-end">
                    <button type="button" className="sp-btn ghost green" onClick={async () => {
                      try {
                        const res = await axios.post(`/api/seasons/${season.id}/test-teamspeak`);
                        setAlertModal({ show: true, message: res.data.message || 'Connection test result', type: res.data.success ? 'success' : 'error' });
                      } catch (err) {
                        setAlertModal({ show: true, message: err.response?.data?.error || 'Connection test failed', type: 'error' });
                      }
                    }}>{t('createSeason.testConnection', 'Test Connection')}</button>
                  </div>
                </div>
                <div className="sp-hint">{t('seasonPage.tsNote', 'Coach names are set per team in Team Settings. TS nickname must match exactly. Test Connection uses the saved settings.')}</div>
              </>
            )}

            <div className="sp-sub">Akros AC</div>
            <ToggleRow label={t('createSeason.enableAkros', 'Enable Akros AC')} desc={t('createSeason.enableAkrosHint', 'Automatically register servers with Akros anticheat on match start')}
              on={formData.akros_enabled} onChange={v => set({ akros_enabled: v })} />
            {formData.akros_enabled && (
              <Field label={t('createSeason.licenseKey', 'License Key')} hint={t('seasonPage.akrosNote', 'Server IP, port, and RCON password are sent automatically per server when a match starts.')}>
                <div className="sp-pw">
                  <input className="sp-input" type={showAkrosKey ? 'text' : 'password'} placeholder={t('createSeason.akrosKeyPlaceholder', 'Enter Akros license key')}
                    value={formData.akros_license_key} onChange={e => set({ akros_license_key: e.target.value })} />
                  <button type="button" onClick={() => setShowAkrosKey(!showAkrosKey)} aria-label={showAkrosKey ? 'Hide key' : 'Show key'}>
                    {showAkrosKey ? <IconEyeOff size={16} color="#64748b" /> : <IconEye size={16} color="#64748b" />}
                  </button>
                </div>
              </Field>
            )}

            <div className="sp-sub">HLTV</div>
            <ToggleRow label={t('createSeason.enableHltv', 'Enable HLTV')} desc={t('createSeason.enableHltvHint', 'Execute HLTV commands on the server when a match is created')}
              on={formData.hltv_enabled} onChange={v => set({ hltv_enabled: v })} />
            {formData.hltv_enabled && (
              <Field label={t('createSeason.commandsOnePerLine', 'Commands (one per line)')} hint={t('seasonPage.hltvNote', 'Paste the commands provided by HLTV. Each line is executed as a separate RCON command on match start.')}>
                <textarea className="sp-input mono" rows={7} value={formData.hltv_commands} onChange={e => set({ hltv_commands: e.target.value })}
                  placeholder={'sv_logfile 1\nlog on\nmp_logdetail 3\nmp_logmoney 1\nmp_logdetail_items 1\nmp_disconnect_kills_players 0\nlogaddress_add_http "http://..."'} />
              </Field>
            )}
          </>
        ) })}
      </div>

      <div className={`sp-savebar${dirty ? ' dirty' : ''}`}>
        <span className="sp-savebar-text">
          {dirty ? t('seasonPage.unsaved', 'Unsaved changes') : (savedAt ? t('seasonPage.saved', 'All changes saved') : t('seasonPage.noChanges', 'No changes'))}
        </span>
        <button type="button" className="sp-btn ghost" disabled={!dirty || saving} onClick={discard}>{t('seasonPage.discard', 'Discard')}</button>
        <button type="submit" className="sp-btn" disabled={!dirty || saving}>{saving ? t('seasonPage.saving', 'Saving…') : t('createSeason.saveChanges', 'Save Changes')}</button>
      </div>

      <AlertModal show={alertModal.show} message={alertModal.message} type={alertModal.type} onClose={() => setAlertModal({ ...alertModal, show: false })} />
    </form>
  );
}
